import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import products, { initializeProducts } from '../data/products';
import ProductImage from '../components/ProductImage';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentProducts, setCurrentProducts] = useState(products);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Load fresh product data
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const latestProducts = await initializeProducts();
        setCurrentProducts(latestProducts);
        
        // Find the product from all categories
        const allProducts = [
          ...latestProducts.smoothies,
          ...latestProducts.juices,
          ...latestProducts.proteinBalls
        ];
        
        const foundProduct = allProducts.find(p => p.id === id || p._id === id);
        setProduct(foundProduct);
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProducts();
  }, [id]);
  
  if (loading) {
    return (
      <div className="product-loading">
        <h2>Loading Product...</h2>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="product-not-found">
        <h2>Product Not Found</h2>
        <p>Sorry, the product you're looking for doesn't exist or has been removed.</p>
        <Link to="/menu" className="back-link">Back to Menu</Link>
      </div>
    );
  }
  
  const handleAddToOrder = () => {
    navigate(`/order?product=${product.id}`);
  };
  
  // Map category name to readable format
  const getCategoryName = (category) => {
    switch(category) {
      case 'smoothies':
        return 'Smoothie';
      case 'juices':
        return 'Juice';
      case 'proteinBalls':
        return 'Protein Ball';
      default:
        return category;
    }
  };
  
  return (
    <div className="product-detail-page">
      <div className="product-detail-container">
        <div className="product-image-container">
          <ProductImage product={product} className="large-image" />
        </div>
        
        <div className="product-info-container">
          <div className="product-category">{getCategoryName(product.category)}</div>
          <h1>{product.name}</h1>
          <p className="product-description">{product.description}</p>
          
          <div className="product-price">${product.price.toFixed(2)}</div>
          
          <div className="product-actions">
            <button onClick={handleAddToOrder} className="add-to-order-btn">
              Add to Order
            </button>
            <Link to="/menu" className="back-to-menu">
              Back to Menu
            </Link>
          </div>
          
          <div className="product-details">
            <div className="ingredients">
              <h3>Ingredients</h3>
              <ul>
                {product.ingredients.map((ingredient, idx) => (
                  <li key={idx}>{ingredient}</li>
                ))}
              </ul>
            </div>
            
            <div className="nutrition-facts">
              <h3>Nutrition Information</h3>
              <ul>
                <li><strong>Calories:</strong> {product.nutritionFacts.calories}</li>
                <li><strong>Protein:</strong> {product.nutritionFacts.protein}g</li>
                <li><strong>Fat:</strong> {product.nutritionFacts.fat}g</li>
                <li><strong>Carbs:</strong> {product.nutritionFacts.carbs}g</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="related-products">
        <h2>You Might Also Like</h2>
        <div className="related-grid">
          {(() => {
            // Get all products from current state
            const allProducts = [
              ...currentProducts.smoothies || [],
              ...currentProducts.juices || [],
              ...currentProducts.proteinBalls || []
            ];
            
            return allProducts
              .filter(p => p.category === product.category && p.id !== product.id)
              .slice(0, 3)
              .map(relatedProduct => (
              <div className="related-card" key={relatedProduct.id}>
                <div className="related-image">
                  <ProductImage product={relatedProduct} />
                </div>
                <h3>{relatedProduct.name}</h3>
                <p>${relatedProduct.price.toFixed(2)}</p>
                <Link to={`/product/${relatedProduct.id}`} className="view-product">
                  View Details
                </Link>
              </div>
            ))
          })()}
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;